<template>
  <div>
    <opq-basic-appbar>Paiement de facture</opq-basic-appbar>
    <v-main>
      <v-sheet class="mx-auto pa-3 pa-sm-6 pa-md-12 mt-md-8 facture" outlined max-width="900">
        <h1 class="display-1 mb-3">Payer une facture</h1>
        <v-divider class="my-3"></v-divider>

        <div class="text-center my-12">
          <template v-if="loading">
            <v-skeleton-loader type="text" width="100" class="mx-auto"></v-skeleton-loader>
            <v-skeleton-loader type="image" width="200" height="50" class="mb-3 mx-auto"></v-skeleton-loader>
          </template>
          <template v-else>
            <template v-if="facture.solde > 0">
              <div v-if="!facture.jetonDeFacturation">
                <span class="caption text-uppercase grey--text">solde à payer{{ appels.length > 1 ? ` en ${appels.length} versements` : "" }}</span>
                <h2 class="display-3 primary--text mb-3">
                  {{ facture.solde | money }}
                </h2>
              </div>
              <template v-if="appels.length > 1">
                <EcheancierDePaiement :appels="appels" />
                <p v-if="facture.solde < facture.total" class="mt-8 text-body-2 text-left">
                  <b>
                    Votre premier versement a été prélevé sur votre carte de crédit et le deuxième le sera automatiquement le 30 juin.<br />
                    Si toutefois, vous souhaitez payer ce deuxième versement avant le 30 juin, vous pouvez accéder, en tout temps, à la
                    facture via votre portail (Facturation - Paiements à faire) et cliquer sur le bouton ci-dessous "Payer le solde".</b>
                </p>
              </template>
            </template>
            <template v-else>
              <h1 class="display-2 aleo">
                <v-icon x-large color="success">mdi-checkbox-marked-circle-outline</v-icon>
                Facture payée, merci !
              </h1>
            </template>
          </template>
          <template v-if="facture.solde > 0">
            <v-sheet flat class="overflow-hidden d-flex flex-column">
              <v-dialog v-model="showMonerisCheckout" persistent hide-overlay scrollable fullscreen :key="monerisDialogKey">
                <template v-slot:activator="{ on, attrs }">
                  <v-btn color="primary" dark v-on="on" v-bind="attrs" x-large tile class="mt-4 mx-auto" :loading="paiementEnCours">
                    {{ appels.length > 1 && facture.solde == facture.total ? "Enregistrer" : "Payer le solde" }} 
                  </v-btn>
                </template>
                <MonerisCheckout v-if="showMonerisCheckout" :idFacture="idFacture" @paiementEudonetStart="paiementEudonetStart" @paiementEudonetComplete="paiementEudonetComplete"></MonerisCheckout>
              </v-dialog>
          </v-sheet>
          </template>
        </div>
        <span class="caption text-uppercase grey--text">à</span>
        <template v-if="loading">
          <v-skeleton-loader type="text@3" width="200"></v-skeleton-loader>
        </template>
        <template v-else-if="facture.contact">
          <div class="mb-1">{{ facture.contact.nom }}</div>
          <div class="mb-1">{{ facture.contact.courriel }}</div>
          <div class="mb-1">{{ facture.contact.telephone }}</div>
        </template>

        <v-divider class="my-3"></v-divider>
        <span class="caption text-uppercase grey--text">date d'émission</span>
        <template v-if="loading">
          <v-skeleton-loader type="text" width="150"></v-skeleton-loader>
        </template>
        <template v-else>
          <div>{{ facture.date | date }}</div>
        </template>

        <v-divider class="my-3"></v-divider>
        <span class="caption text-uppercase grey--text">numéro</span>

        <template v-if="loading">
          <v-skeleton-loader type="text" width="150"></v-skeleton-loader>
        </template>
        <template v-else>
          <div>{{ facture.reference }}</div>
        </template>

        <v-divider class="my-3"></v-divider>
        <span class="caption text-uppercase grey--text">détails</span>
        <template v-if="loading">
          <v-skeleton-loader type="text@4" width="150"></v-skeleton-loader>
        </template>
        <template v-else>
          <div class="my-1">
            <div v-for="(detail, i) in facture.details" :key="'detail' + i" class="detail-group">
              <span class="detail-description mr-3">
                <span>{{ detail.libelle }}</span>
                <span class="secondary--text ml-3 caption" v-if="detail.quantite">x{{ detail.quantite }}</span>
              </span>

              <span>{{ detail.prixTotal | money }}</span>
            </div>
          </div>

          <v-divider width="450"></v-divider>

          <div class="detail-group my-1">
            <span class="detail-description">Total avant taxes</span>
            <span class="detail-montant">{{ facture.montantHT | money }}</span>
          </div>

          <div class="detail-group my-1" v-if="facture.rabais > 0">
            <span class="detail-description">Remise</span>
            <span class="detail-montant">{{ -facture.rabais | money }}</span>
          </div>
          <v-divider width="450"></v-divider>

          <div class="my-1">
            <div v-for="(taxe, i) in facture.taxes" :key="'taxe' + i" class="detail-group">
              <span class="detail-description"> {{ taxe.code }} </span>
              <span>{{ taxe.montant | money }}</span>
            </div>
          </div>
          <v-divider width="450"></v-divider>

          <div class="detail-group my-1" v-if="facture.dejaPaye > 0">
            <span class="detail-description">Total</span>
            <span class="detail-montant">{{ facture.total | money }}</span>
          </div>

          <div class="detail-group my-1" v-if="facture.dejaPaye > 0">
            <span class="detail-description">Payé</span>
            <span class="detail-montant">{{ facture.dejaPaye | money }}</span>
          </div>

          <div class="detail-group my-1 font-weight-bold">
            <span class="detail-description">Solde</span>
            <span class="detail-montant">{{ facture.solde | money }}</span>
          </div>
        </template>
      </v-sheet>
    </v-main>
  </div>
</template>

<script>
import { Api } from "@/plugins/api"
import EcheancierDePaiement from "@/components/Facture/EcheancierDePaiement"
import MonerisCheckout from "@/components/Facture/Moneris/MonerisCheckout"
export default {
  components: { EcheancierDePaiement, MonerisCheckout },
  data() {
    return {
      facture: {},
      showMonerisCheckout: false,
      monerisDialogKey: 0,
      paiementEnCours: false,
      loading: true,
    }
  },
  computed: {
    appels() {
      return this.facture ? this.facture.listeEcheanciers : []
    },
    idFacture() {
      return this.$route.params.idFacture
    },
    payable() {
      return this.facture && this.facture.solde > 0 && this.facture.dejaPaye < this.facture.total
    }
  },
  created() {
    this.chargerLaFacture()
  },
  methods: {
    chargerLaFacture() {
      if (this.idFacture === undefined) {
        this.$router.push({ path: "erreur-facture-id" })
        return
      }

      this.loading = true
      this.getFacture(this.idFacture)
        .then(async (response) => {
          this.facture = response.data
        })
        .catch((error) => {
          if (error.response.status === 404) {
            this.$router.push({
              name: "NotFound",
              message: error.response.status
            })
          } else {
            this.$router.push({
              name: "InternalError",
              message: error.response.message
            })
          }
        })
        .finally(() => {
          this.loading = false
        })
    },
    getFacture: (factureId) => Api.get("moneris-checkout/" + factureId + "/invoice"),
    paiementEudonetStart() {
      this.paiementEnCours = true
    },
    paiementEudonetComplete() {
      this.chargerLaFacture()
      this.showMonerisCheckout = false
      this.monerisDialogKey += 1
      this.paiementEnCours = false
    },
  }
}
</script>

<style>
.facture .detail-group {
  display: flex;
  max-width: 450px;
  justify-content: space-between;
  align-content: space-between;
}
</style>
